import { type ExplodeFlow, NodeRole } from "../../types";
import type { AssetRecord } from "./buildAssetDocsGraph";
import {
    COLUMN_GAP,
    columnsForAspect,
    GRID_TOP,
    graphNode,
    NODE_WIDTH,
    OVERLAY_Z,
    ROOT_Y,
    ROW_GAP,
    titleEdge,
    valueEdge,
} from "./graphLayout";

export interface ChainStep {
    label: string;
    caption?: string;
}

const LEAF_ID = "asset-parent";

const GHOST_ASSETS = 6;

export const buildAssetGraph = ({
    assets,
    chain,
    assetCount,
    loading = false,
}: {
    assets: AssetRecord[];
    chain: ChainStep[];
    assetCount?: number;
    loading?: boolean;
}): ExplodeFlow => {
    const showGhosts = loading && assets.length === 0;
    const cells = showGhosts ? GHOST_ASSETS : assets.length;
    const columns = columnsForAspect(cells || 1);
    const stepX = NODE_WIDTH + COLUMN_GAP;
    const gridWidth = Math.min(cells || 1, columns) * stepX - COLUMN_GAP;
    const leafX = (gridWidth - NODE_WIDTH) / 2;

    const chainNodes = chain.map((step, index) => {
        const isLeaf = index === chain.length - 1;
        return graphNode(
            isLeaf ? LEAF_ID : `chain-${index}`,
            leafX - (chain.length - 1 - index) * stepX,
            ROOT_Y,
            {
                label: step.label,
                caption: step.caption,
                role: index === 0 ? NodeRole.ROOT : NodeRole.GROUP,
                focused: isLeaf,
                count: isLeaf ? (assetCount ?? assets.length) : undefined,
                selection: [],
            },
            OVERLAY_Z,
        );
    });

    const chainEdges = chainNodes
        .slice(1)
        .map((step, index) => titleEdge(chainNodes[index].id, step.id, `e-chain-${index}`));

    const ghostNodes = Array.from({ length: showGhosts ? GHOST_ASSETS : 0 }, (_, index) =>
        graphNode(
            `ast-ghost-${index}`,
            (index % columns) * stepX,
            GRID_TOP + Math.floor(index / columns) * ROW_GAP,
            { label: "", role: NodeRole.GROUP, selection: [], skeleton: true },
            OVERLAY_Z,
        ),
    );

    const assetNodes = assets.map((asset, index) =>
        graphNode(
            `ast-${index}`,
            (index % columns) * stepX,
            GRID_TOP + Math.floor(index / columns) * ROW_GAP,
            {
                label: asset.label,
                role: NodeRole.GROUP,
                assetId: asset.id,
                homeUrl: asset.homeUrl,
                selection: [],
            },
            OVERLAY_Z,
        ),
    );

    const bodyNodes = [...ghostNodes, ...assetNodes];
    const assetEdges = bodyNodes.map((bodyNode, index) =>
        valueEdge(LEAF_ID, bodyNode.id, `e-ast-${index}`),
    );

    return {
        nodes: [...chainNodes, ...bodyNodes],
        edges: [...chainEdges, ...assetEdges],
    };
};
